import { useEffect } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import { CheckCircle, ChevronRight, MapPin } from 'lucide-react'
import { getCityBySlug, getNearbyCities } from '@/data/ukCities'
import SiluxPromise from '@/components/SiluxPromise'
import { setMetaTags, injectSchema } from '@/utils/seo'

const PLANS = [
  { name: "Data Only", data: "30GB", price: "£5", features: ["30GB data", "No calls/texts", "4G/5G", "30-day or contract"] },
  { name: "Calls + Data", data: "Unlimited calls", price: "£8", features: ["30GB data", "Unlimited calls & texts", "4G/5G", "30-day or contract"], popular: true },
  { name: "Unlimited", data: "Unlimited", price: "£18", features: ["Unlimited data", "Unlimited calls & texts", "5G", "Roaming extra — varies by package"] },
]

export default function GeoSimCards() {
  const { city: citySlug } = useParams<{ city: string }>()
  const city = getCityBySlug(citySlug ?? '')

  if (!city) return <Navigate to="/cheap-sim-cards" replace />

  const nearby = getNearbyCities(city)
  const isScotland = city.region === 'Scotland'
  const isWales = city.region === 'Wales'
  const isNI = city.region === 'Northern Ireland'
  const regionLabel = isScotland ? 'Scotland' : isWales ? 'Wales' : isNI ? 'Northern Ireland' : 'England'

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": `Cheap SIM Cards in ${city.name}`,
        "provider": { "@type": "Organization", "name": "Silux Mobile", "url": "https://www.siluxmobile.co.uk" },
        "serviceType": "SIM Card",
        "areaServed": { "@type": "City", "name": city.name, "containedInPlace": { "@type": "Country", "name": "United Kingdom" } },
        "description": `Cheap SIM cards available in ${city.name}, ${city.county}. Data SIMs from £5/month on Three's 5G network. No contract. Free Tracked 48 delivery to ${city.name}.`,
        "url": `https://www.siluxmobile.co.uk/cheap-sim-cards/${city.slug}`,
        "offers": { "@type": "AggregateOffer", "lowPrice": "5", "highPrice": "28", "priceCurrency": "GBP" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": `What is the cheapest SIM card in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Silux Mobile offers the cheapest SIM cards available in ${city.name} — data SIMs from £5/month on Three, Vodafone, EE and O2, on a 30-day rolling plan or a 3 to 24-month contract, with free Royal Mail Tracked 48 delivery to ${city.county}.` }
          },
          {
            "@type": "Question",
            "name": `Does Three have good coverage in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Yes. Three's 4G and 5G network covers ${city.name} and the surrounding ${city.county} area with strong indoor and outdoor signal. You can check your exact postcode at siluxmobile.co.uk/network-coverage.` }
          },
          {
            "@type": "Question",
            "name": `Can I get a SIM card delivered to ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Yes. Silux Mobile delivers SIM cards to all ${city.name} addresses free by Royal Mail Tracked 48 (2–3 working days). Faster delivery can be chosen at checkout.` }
          },
          {
            "@type": "Question",
            "name": `Do Silux Mobile SIM cards work in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Yes. Silux Mobile SIMs are available on Three, Vodafone, EE and O2, so you can choose the network with the best coverage across ${city.name}, ${city.county} and all of ${regionLabel}.` }
          },
          {
            "@type": "Question",
            "name": `Will my SIM price increase during my contract?`,
            "acceptedAnswer": { "@type": "Answer", "text": `No. Silux Mobile never raises your price mid-contract — there are no price increases during your contract and no annual or inflation-linked price rises. The price you sign up at in ${city.name} is the price you keep.` }
          },
          {
            "@type": "Question",
            "name": `Are SIM plan upgrades free with Silux Mobile?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Yes. Upgrades are free — move to a bigger data plan or a better SIM whenever you like at no extra cost, with no upgrade or admin fees.` }
          },
          {
            "@type": "Question",
            "name": `Do you run a credit check for SIM cards in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `No. There are no credit checks on our SIM contracts, so anyone in ${city.name} can join Silux Mobile regardless of credit history. No credit check is required to order or activate your SIM.` }
          },
          {
            "@type": "Question",
            "name": `Who is the best SIM only provider in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Silux Mobile is a top SIM only and mobile provider in ${city.name}, with SIMs on Three, Vodafone, EE and O2 — cheap SIM deals on 30-day rolling plans or 3 to 24-month contracts, no credit checks and no price rises.` }
          },
          {
            "@type": "Question",
            "name": `Are Silux Mobile SIM prices in ${city.name} inclusive of VAT?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Yes. There are no hidden fees — every Silux Mobile SIM price shown to customers in ${city.name} is the price you pay, incl. VAT. No setup fees, no surprise mid-contract increases.` }
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.siluxmobile.co.uk" },
          { "@type": "ListItem", "position": 2, "name": "Cheap SIM Cards", "item": "https://www.siluxmobile.co.uk/cheap-sim-cards" },
          { "@type": "ListItem", "position": 3, "name": city.name, "item": `https://www.siluxmobile.co.uk/cheap-sim-cards/${city.slug}` }
        ]
      }
    ]
  }

  useEffect(() => {
    const canonical = `https://www.siluxmobile.co.uk/cheap-sim-cards/${city.slug}`
    setMetaTags({
      title: `Cheap SIM Cards in ${city.name} | SIM Only Provider | Silux Mobile`,
      description: `Silux Mobile is a SIM only provider and mobile network provider in ${city.name}. Cheapest data SIMs from £5/month on Three's 5G network. No contract. Free Tracked 48 delivery to ${city.county}. Order today.`,
      keywords: [`cheap sim cards ${city.name}`, `sim only deals ${city.name}`, `data sim ${city.name}`, `mobile network ${city.name}`, 'cheap sim cards uk', 'no contract sim', 'three 5g sim only'],
      canonical,
      ogUrl: canonical,
    })
    injectSchema(JSON.stringify(schema), 'geo-sim-schema')
    return () => { const el = document.getElementById('geo-sim-schema'); if (el) el.remove() }
  }, [city.slug])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-700 to-indigo-900 text-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <nav className="text-indigo-300 text-sm mb-6 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/cheap-sim-cards" className="hover:text-white">Cheap SIM Cards</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{city.name}</span>
          </nav>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-indigo-300" />
            <span className="text-indigo-300 text-sm">{city.county}, {regionLabel}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Cheap SIM Cards in {city.name}
          </h1>
          <p className="text-xl text-indigo-100 mb-3 max-w-2xl">
            The cheapest SIM cards in {city.name} — from £5/month on Three's 5G network. Free Royal Mail Tracked 48 delivery to {city.county}. 30-day rolling or 3 to 24-month contracts, no credit check required.
          </p>
          <p className="text-indigo-200 text-sm mb-8 max-w-2xl">
            Silux Mobile is a SIM only provider and mobile network provider in {city.name}, with SIM cards available UK-wide including {city.name} and all of {city.county}. SIMs are available on Three, Vodafone, EE and O2 — the same masts and signal as the networks themselves, at a fraction of the price. Choose your plan and it'll arrive at your {city.name} address by free Royal Mail Tracked 48 delivery (2–3 working days), or choose faster delivery at checkout.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/sim-only" className="bg-white text-indigo-800 font-semibold px-6 py-3 rounded-lg hover:bg-indigo-50 transition">
              See All Plans
            </Link>
            <Link to="/sim-only" className="border border-white text-white px-6 py-3 rounded-lg hover:bg-white/10 transition">
              Get a Free SIM
            </Link>
            <Link to="/network-coverage" className="border border-white/50 text-indigo-200 px-6 py-3 rounded-lg hover:bg-white/10 transition text-sm">
              Check {city.name} Coverage
            </Link>
          </div>
        </div>
      </section>

      {/* Why Silux in this city */}
      <section className="py-14 px-4 bg-indigo-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Why {city.name} Customers Choose Silux Mobile</h2>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { title: `Delivered to ${city.name}`, body: "Free Royal Mail Tracked 48 delivery (2–3 working days) anywhere in the UK including " + city.name + " and " + city.county + ".", icon: "📬" },
              { title: "Three 5G in " + city.name, body: "Our SIMs use Three's 5G/4G network with strong coverage across " + city.name + " and surrounding areas.", icon: "📶" },
              { title: "From £5/Month", body: "Cheaper than Lebara, Giffgaff and SMARTY — with 5G included. Roaming is an extra cost and varies by package.", icon: "💷" },
              { title: "Flexible Plans", body: "SIM cards on 30-day rolling plans or 3 to 24-month contracts, and broadband from 1 month.", icon: "🔓" },
            ].map(c => (
              <div key={c.title} className="bg-white rounded-xl p-5 shadow-sm text-center">
                <div className="text-3xl mb-3">{c.icon}</div>
                <h3 className="font-bold text-gray-900 text-sm mb-2">{c.title}</h3>
                <p className="text-gray-500 text-xs">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiluxPromise variant="sim" />

      {/* Plans */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">SIM Plans — Delivered to {city.name}</h2>
          <p className="text-gray-500 text-center mb-10">All on Three's 5G network. 30-day rolling plans or 3 to 24-month contracts.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map(plan => (
              <div key={plan.name} className={`rounded-2xl p-6 border-2 ${plan.popular ? 'border-indigo-500 shadow-lg' : 'border-gray-200'} relative`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</div>}
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <p className="text-sm text-gray-500 mb-2">{plan.data}</p>
                <p className="text-3xl font-bold text-indigo-700 mb-4">{plan.price}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-indigo-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/sim-only" className={`block text-center py-2.5 rounded-lg font-semibold text-sm transition ${plan.popular ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                  Order to {city.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-14 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Three 5G Coverage in {city.name}</h2>
          <p className="text-gray-600 mb-6 max-w-xl mx-auto">
            Three's 5G and 4G network covers {city.name} and {city.county} with strong indoor and outdoor signal. With over 99% UK population coverage, you'll have reliable connectivity whether you're in the city centre or the surrounding areas.
          </p>
          <Link to="/network-coverage" className="inline-block bg-indigo-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-indigo-700 transition">
            Check Your {city.name} Postcode
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">SIM Cards in {city.name} — FAQs</h2>
          <div className="space-y-3">
            {(schema["@graph"][1] as any).mainEntity.map((q: any) => (
              <details key={q.name} className="bg-gray-50 rounded-xl border border-gray-200 p-5 group">
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex justify-between items-center text-sm">
                  {q.name}
                  <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 ml-2" />
                </summary>
                <p className="text-gray-600 text-sm mt-3">{q.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby cities */}
      {nearby.length > 0 && (
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">Cheap SIM Cards Near {city.name}</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {nearby.map(c => (
                <Link key={c.slug} to={`/cheap-sim-cards/${c.slug}`} className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-700 hover:border-indigo-400 hover:text-indigo-700 transition">
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cross-links */}
      <section className="py-12 px-4 bg-indigo-700 text-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-bold mb-4 text-center">More from Silux Mobile</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { to: "/data-sim-cards",        label: "Data SIM Cards" },
              { to: "/no-contract-sim",        label: "No Contract SIM" },
              { to: `/mobile-broadband/${city.slug}`, label: `Broadband in ${city.name}` },
              { to: "/sim-only",               label: "Free SIM Card" },
            ].map(l => (
              <Link key={l.to} to={l.to} className="bg-white/10 hover:bg-white/20 rounded-lg px-4 py-3 text-center text-sm font-medium transition">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
